import { GridColDef } from '@mui/x-data-grid-pro';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActions } from '../../../hooks/useActions';
import { useStore } from '../../../hooks/useStore';
import { TableDataGrid, TableDataGridProps } from '../../../lib/components/compound/tableDataGrid';
import Util from '../../../misc/util';
import { useStepper } from '../partials/actionStepperPartial';

export const EmployeeTable: FC = observer(() => {
  const { employeeStore } = useStore();
  const { selectedUsers, bulkUpdateType, bulkUpdateValue, bulkUpdateDate } = useActions();
  const { t } = useTranslation();
  const { activeStep } = useStepper();
  const [rows, setRows] = useState<TableDataGridProps['rows']>();

  const getRows = async () => {
    const employees = await employeeStore.fetchList([
      'available_benefit_budget',
      'first_name',
      'id',
      'last_name'
    ]);
    setRows(
      employees
        ?.filter((employee) => employee.id && selectedUsers?.includes(employee.id))
        .map((employee) => ({
          id: employee.id,
          employee: `${employee.first_name} ${employee.last_name}`,
          current_budget: employee.available_benefit_budget,
          new_budget: Util.calcNewBudget({
            current: employee.available_benefit_budget,
            type: bulkUpdateType,
            increase: bulkUpdateValue as number
          }),
          changed_after: bulkUpdateDate && bulkUpdateDate !== '' ? bulkUpdateDate : new Date()
        }))
    );
  };

  useEffect(() => {
    getRows();
  }, [bulkUpdateValue, bulkUpdateDate]);

  const columns = useMemo<Array<GridColDef>>(
    () => [
      {
        field: 'employee',
        headerName: t('employee:EMPLOYEES'),
        flex: 2,
        cellClassName: () => (activeStep === 1 ? 'pxtra-bold' : 'pxtra')
      },
      {
        field: 'current_budget',
        headerName: t('employee:CURRENT_BUDGET'),
        flex: 1,
        type: 'number',
        valueGetter: ({ value }: { value: number }) => Util.formatCurrency(value)
      },
      {
        field: 'new_budget',
        headerName: t('employee:NEW_BUDGET'),
        flex: 1,
        type: 'number',
        valueGetter: ({ value }: { value: number }) => Util.formatCurrency(value),
        cellClassName: () => (activeStep === 1 ? 'pxtra-bold' : 'pxtra')
      },
      {
        field: 'changed_after',
        headerName: t('common:CHANGED_AFTER'),
        flex: 1,
        valueGetter: ({ value }: { value: string }) => Util.formatDate(value),
        cellClassName: () => (activeStep === 1 ? 'pxtra-bold' : 'pxtra')
      }
    ],
    [activeStep]
  );

  return (
    <TableDataGrid
      rows={rows || []}
      columns={columns}
      checkboxSelection={false}
      hideFooter
      components={{ Toolbar: null }}
    />
  );
});
